import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const LANGS = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

/**
 * Compact language switcher — drops into the public booking header and the
 * admin top-bar / sidebar. The selection is persisted automatically by
 * i18next-browser-languagedetector under `paydaai_lang_v1`.
 *
 * When called from an authenticated context, the preference is also
 * synced to the user profile (`PATCH /api/auth/me/language`) so e-mails
 * are sent in the same language. We swallow auth/network errors silently —
 * the public booking page uses the same component but has no session.
 */
export default function LanguageSwitcher({ variant = 'full', className = '' }) {
  const { i18n, t } = useTranslation();
  const current = LANGS.find((l) => l.code === i18n.resolvedLanguage) || LANGS[0];

  const handleChange = async (code) => {
    if (code === i18n.resolvedLanguage) return;
    i18n.changeLanguage(code);
    try {
      await axios.patch(
        `${API_URL}/api/auth/me/language`,
        { language: code },
        { withCredentials: true },
      );
    } catch {
      /* user is on public page or session expired — harmless */
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md border border-[#E5E5E5] bg-white hover:bg-[#F9FAFB] transition ${className}`}
          aria-label={t('languageSwitcher.label')}
          data-testid="language-switcher"
        >
          {variant === 'compact' ? (
            <>
              <span className="text-base leading-none">{current.flag}</span>
              <span className="uppercase text-xs font-semibold tracking-wider text-[#595959]">{current.code}</span>
            </>
          ) : (
            <>
              <Globe className="w-4 h-4 text-[#2563EB]" />
              <span className="text-base leading-none">{current.flag}</span>
              <span className="hidden sm:inline text-sm text-[#0F172A]">{current.label}</span>
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => handleChange(l.code)}
            className="cursor-pointer"
            data-testid={`language-switcher-option-${l.code}`}
          >
            <span className="text-base mr-2">{l.flag}</span>
            <span className="flex-1">{l.label}</span>
            {i18n.resolvedLanguage === l.code && <Check className="w-4 h-4 text-[#2563EB]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
